// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

/**
 * Validates if an S3 presigned URL is expired
 * @param presignedUrl - The S3 presigned URL to validate
 * @returns true if the URL is expired, false otherwise
 */
const isS3PresignedUrlExpired = (presignedUrl: string): boolean => {
  try {
    // Parse the URL to extract query parameters
    const url = new URL(presignedUrl);
    const { searchParams } = url;

    // Extract the X-Amz-Date parameter (creation time)
    const amzDate = searchParams.get('X-Amz-Date');
    if (!amzDate) {
      console.warn('S3 presigned URL missing X-Amz-Date parameter');
      return true; // Consider expired if we can't determine creation time
    }

    // Extract the X-Amz-Expires parameter (expiry duration in seconds)
    const amzExpires = searchParams.get('X-Amz-Expires');
    if (!amzExpires) {
      console.warn('S3 presigned URL missing X-Amz-Expires parameter');
      return true; // Consider expired if we can't determine expiry duration
    }

    // Parse the creation date (format: YYYYMMDDTHHMMSSZ)
    const year = parseInt(amzDate.substring(0, 4), 10);
    const month = parseInt(amzDate.substring(4, 6), 10) - 1; // Month is 0-indexed
    const day = parseInt(amzDate.substring(6, 8), 10);
    const hour = parseInt(amzDate.substring(9, 11), 10);
    const minute = parseInt(amzDate.substring(11, 13), 10);
    const second = parseInt(amzDate.substring(13, 15), 10);

    const creationDate = new Date(Date.UTC(year, month, day, hour, minute, second));
    const expiryDurationSeconds = parseInt(amzExpires, 10);

    // Calculate expiry time
    const expiryDate = new Date(creationDate.getTime() + (expiryDurationSeconds * 1000));

    // Check if current time is past expiry time
    const currentTime = new Date();
    const isExpired = currentTime > expiryDate;

    return isExpired;
  } catch (error) {
    console.error('Error validating S3 presigned URL:', error);
    return true; // Consider expired if there's an error parsing the URL
  }
};

export default isS3PresignedUrlExpired;
